package com.merkost.handpass.admin.api

import com.merkost.handpass.api.dto.admin.*
import com.merkost.handpass.api.dto.listing.CategoryResponse
import com.merkost.handpass.api.dto.listing.ListingImageDto
import com.merkost.handpass.api.dto.listing.ListingResponse
import com.merkost.handpass.api.dto.report.ReportResponse
import com.merkost.handpass.api.dto.report.UserReportResponse
import com.merkost.handpass.api.dto.storage.UploadUrlResponse
import com.merkost.handpass.api.dto.user.ProfileResponse
import com.merkost.handpass.api.enums.AccountStatus
import com.merkost.handpass.api.enums.ContentFlag
import com.merkost.handpass.api.enums.ImageStatus
import com.merkost.handpass.api.enums.ListingStatus
import com.merkost.handpass.api.enums.ProfileVisibility
import kotlinx.coroutines.delay

class MockAdminRepository : AdminRepositoryApi {

    override suspend fun getStats(): AdminStatsResponse = AdminStatsResponse(
        totalUsers = 1284,
        activeListings = 342,
        pendingListingReports = 5,
        pendingUserReports = 2,
        actionsToday = 18,
        totalQueueSize = 7,
        recentActions = listOf(
            RecentActionEntry("a-001", "Priya Nair", "SUSPEND_LISTING", "listing", "l-014", "Counterfeit AirPods Pro 2", "Counterfeit product", "2026-06-17T09:42:00Z"),
            RecentActionEntry("a-002", "Marcus Webb", "BAN_USER", "user", "u-009", "Dmitri Volkov", "Repeated scam reports", "2026-06-17T09:18:00Z"),
            RecentActionEntry("a-003", "Priya Nair", "WARN_USER", "user", "u-004", "Liam O'Connor", "Spam in messages", "2026-06-17T08:55:00Z"),
            RecentActionEntry("a-004", "Sofia Reyes", "DISMISS_REPORT", "listing", "l-007", "Vintage Leather Sofa", "No violation found", "2026-06-17T08:31:00Z"),
            RecentActionEntry("a-005", "Marcus Webb", "RESTORE_LISTING", "listing", "l-003", "iPhone 13 Pro 256GB", "Appeal approved", "2026-06-17T07:49:00Z"),
            RecentActionEntry("a-006", "Sofia Reyes", "SUSPEND_USER", "user", "u-012", "Chen Wei", "Harassment", "2026-06-16T22:14:00Z"),
            RecentActionEntry("a-007", "Priya Nair", "SUSPEND_LISTING", "listing", "l-021", "Designer Handbag Replica", "Counterfeit product", "2026-06-16T20:03:00Z"),
            RecentActionEntry("a-008", "Marcus Webb", "WARN_USER", "user", "u-006", "Aisha Rahman", "Wrong category abuse", "2026-06-16T18:27:00Z"),
        ),
        oldestPendingReportAgeSeconds = 108000,
        pendingOver24h = 2,
        avgTimeToActionSeconds = 14400,
        actionMix = listOf(
            ActionMixEntry("SUSPEND_LISTING", 6),
            ActionMixEntry("WARN_USER", 5),
            ActionMixEntry("DISMISS_REPORT", 4),
            ActionMixEntry("BAN_USER", 2),
            ActionMixEntry("RESTORE_LISTING", 1),
        ),
    )

    override suspend fun getCategoryTree(): List<CategoryResponse> = listOf(
        CategoryResponse(
            id = "cat-electronics",
            name = "Electronics",
            slug = "electronics",
            children = listOf(
                CategoryResponse(id = "cat-phones", parentId = "cat-electronics", name = "Phones", slug = "phones", level = 1),
                CategoryResponse(id = "cat-audio", parentId = "cat-electronics", name = "Audio", slug = "audio", level = 1),
            ),
        ),
        CategoryResponse(
            id = "cat-home",
            name = "Home & Garden",
            slug = "home-garden",
            children = listOf(
                CategoryResponse(
                    id = "cat-furniture",
                    parentId = "cat-home",
                    name = "Furniture",
                    slug = "furniture",
                    level = 1,
                    children = listOf(
                        CategoryResponse(id = "cat-seating", parentId = "cat-furniture", name = "Seating", slug = "seating", level = 2),
                    ),
                ),
            ),
        ),
    )

    override suspend fun createPartnerListing(request: AdminCreateListingRequest): Result<ListingResponse> {
        delay(100)
        return Result.success(
            ListingResponse(
                id = "l-preview-${request.sellerId}",
                categoryId = request.listing.categoryId,
                sellerId = request.sellerId,
                title = request.listing.title,
                description = request.listing.description,
                price = request.listing.price,
                condition = request.listing.condition,
                images = request.listing.images.mapIndexed { index, url -> ListingImageDto("img-preview-$index", url, index) },
                status = ListingStatus.ACTIVE,
                createdAt = "2026-08-25T00:00:00Z",
            ),
        )
    }

    override suspend fun getSellerUploadUrl(sellerId: String, contentType: String, fileSize: Long): UploadUrlResponse? {
        delay(100)
        return UploadUrlResponse(
            uploadUrl = "https://storage.example.test/upload/$sellerId",
            storageKey = "$sellerId/preview-listing-image.jpg",
            expiresIn = 900,
        )
    }

    override suspend fun getUsers(page: Int, pageSize: Int, search: String?, status: String?, sortBy: String?, sortAsc: Boolean): PaginatedResponse<AdminUserListItem> {
        val users = listOf(
            AdminUserListItem("u-001", "Emma Thompson", "emma.thompson@gmail.com", "https://i.pravatar.cc/120?img=1", "active", "2025-11-03T10:24:00Z", 12, 0),
            AdminUserListItem("u-002", "James Carter", "james.carter@outlook.com", "https://i.pravatar.cc/120?img=12", "active", "2025-09-18T14:05:00Z", 7, 0),
            AdminUserListItem("u-003", "Olivia Bennett", "olivia.bennett@yahoo.com", null, "warned", "2025-12-21T08:47:00Z", 4, 1),
            AdminUserListItem("u-004", "Liam O'Connor", "liam.oconnor@gmail.com", "https://i.pravatar.cc/120?img=33", "warned", "2026-01-09T19:33:00Z", 9, 2),
            AdminUserListItem("u-005", "Sophia Martinez", "sophia.martinez@gmail.com", "https://i.pravatar.cc/120?img=5", "active", "2025-08-30T11:12:00Z", 18, 0),
            AdminUserListItem("u-006", "Aisha Rahman", "aisha.rahman@hotmail.com", "https://i.pravatar.cc/120?img=20", "warned", "2026-02-14T07:08:00Z", 3, 1),
            AdminUserListItem("u-007", "Noah Williams", "noah.williams@gmail.com", null, "active", "2025-10-27T16:41:00Z", 6, 0),
            AdminUserListItem("u-008", "Isabella Rossi", "isabella.rossi@gmail.com", "https://i.pravatar.cc/120?img=9", "suspended", "2025-07-15T09:19:00Z", 2, 3),
            AdminUserListItem("u-009", "Dmitri Volkov", "dmitri.volkov@proton.me", "https://i.pravatar.cc/120?img=15", "banned", "2026-03-02T13:55:00Z", 0, 5),
            AdminUserListItem("u-010", "Grace Kim", "grace.kim@gmail.com", "https://i.pravatar.cc/120?img=24", "active", "2025-12-05T20:30:00Z", 11, 0),
            AdminUserListItem("u-011", "Mohammed Al-Farsi", "mohammed.alfarsi@gmail.com", null, "active", "2026-04-19T10:02:00Z", 5, 0),
            AdminUserListItem("u-012", "Chen Wei", "chen.wei@gmail.com", "https://i.pravatar.cc/120?img=11", "suspended", "2025-06-28T18:14:00Z", 1, 4),
            AdminUserListItem("u-013", "Charlotte Davies", "charlotte.davies@gmail.com", "https://i.pravatar.cc/120?img=16", "active", "2026-05-11T09:46:00Z", 8, 0),
            AdminUserListItem("u-014", "Lucas Silva", "lucas.silva@gmail.com", "https://i.pravatar.cc/120?img=51", "active", "2025-11-22T12:38:00Z", 14, 1),
        )
        return PaginatedResponse(
            data = users,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 142, totalPages = 8),
        )
    }

    override suspend fun getUserDetail(userId: String): AdminUserDetail = AdminUserDetail(
        profile = ProfileResponse(
            id = userId,
            email = "olivia.bennett@yahoo.com",
            fullName = "Olivia Bennett",
            avatarUrl = "https://i.pravatar.cc/240?img=47",
            phoneNumber = "+61 412 555 309",
            phoneVerified = true,
            bio = "Selling pre-loved fashion and homewares. Fast shipping, happy to bundle. Based in inner-west Sydney.",
            city = "Sydney",
            country = "Australia",
            rating = 4.6,
            totalSales = 23,
            totalPurchases = 8,
            isVerified = true,
            createdAt = "2025-12-21T08:47:00Z",
            accountStatus = AccountStatus.WARNED,
            profileVisibility = ProfileVisibility.PUBLIC,
            showOnlineStatus = true,
            showLastSeen = true,
            isOnline = false,
            lastSeenAt = "2026-06-17T07:12:00Z",
            slug = "olivia-bennett",
        ),
        listings = listOf(
            AdminListingListItem("l-101", "Mid-century Oak Dining Table", "Olivia Bennett", userId, "Furniture", "active", 45000, "AUD", "2026-05-30T10:00:00Z", 0, listOf("https://picsum.photos/seed/l101/400/400")),
            AdminListingListItem("l-102", "Le Creuset Dutch Oven 24cm", "Olivia Bennett", userId, "Home & Kitchen", "active", 18000, "AUD", "2026-06-02T14:20:00Z", 1, listOf("https://picsum.photos/seed/l102/400/400")),
            AdminListingListItem("l-103", "Vintage Levi's Denim Jacket", "Olivia Bennett", userId, "Fashion", "sold", 8500, "AUD", "2026-04-18T09:05:00Z", 0, listOf("https://picsum.photos/seed/l103/400/400")),
            AdminListingListItem("l-104", "Dyson V8 Cordless Vacuum", "Olivia Bennett", userId, "Electronics", "suspended", 22000, "AUD", "2026-06-10T16:45:00Z", 2, emptyList()),
        ),
        reportsAgainst = listOf(
            UserReportResponse("r-201", userId, "u-007", "Harassment", "Sent aggressive messages after a declined offer.", "pending", "2026-06-15T11:30:00Z", "Olivia Bennett", "https://i.pravatar.cc/120?img=47", "Noah Williams"),
            UserReportResponse("r-202", userId, "u-010", "Spam", "Posting the same item across many categories.", "actioned", "2026-05-28T08:14:00Z", "Olivia Bennett", "https://i.pravatar.cc/120?img=47", "Grace Kim"),
        ),
        moderationHistory = listOf(
            ModerationLogEntry("m-301", "Priya Nair", "WARN_USER", userId, "Olivia Bennett", null, null, "Spam in messages", "2026-06-16T08:55:00Z"),
            ModerationLogEntry("m-302", "Marcus Webb", "SUSPEND_LISTING", userId, "Olivia Bennett", "l-104", "Dyson V8 Cordless Vacuum", "Suspected counterfeit", "2026-06-10T17:02:00Z"),
        ),
    )

    override suspend fun getListings(page: Int, pageSize: Int, search: String?, status: String?, sellerId: String?, sortBy: String?, sortAsc: Boolean): PaginatedResponse<AdminListingListItem> {
        val listings = listOf(
            AdminListingListItem("l-001", "iPhone 13 Pro 256GB Graphite", "James Carter", "u-002", "Electronics", "active", 89900, "AUD", "2026-06-16T10:12:00Z", 0, listOf("https://picsum.photos/seed/l001/400/400")),
            AdminListingListItem("l-002", "Sony WH-1000XM5 Headphones", "Emma Thompson", "u-001", "Electronics", "active", 38000, "AUD", "2026-06-16T08:45:00Z", 0, listOf("https://picsum.photos/seed/l002/400/400")),
            AdminListingListItem("l-003", "Vintage Leather Chesterfield Sofa", "Sophia Martinez", "u-005", "Furniture", "active", 120000, "AUD", "2026-06-15T19:30:00Z", 1, listOf("https://picsum.photos/seed/l003/400/400")),
            AdminListingListItem("l-004", "Counterfeit AirPods Pro 2", "Dmitri Volkov", "u-009", "Electronics", "suspended", 4500, "AUD", "2026-06-15T14:08:00Z", 4, emptyList()),
            AdminListingListItem("l-005", "Trek Marlin 7 Mountain Bike", "Noah Williams", "u-007", "Sports", "active", 65000, "AUD", "2026-06-14T11:22:00Z", 0, listOf("https://picsum.photos/seed/l005/400/400")),
            AdminListingListItem("l-006", "KitchenAid Stand Mixer Red", "Grace Kim", "u-010", "Home & Kitchen", "active", 49900, "AUD", "2026-06-14T09:50:00Z", 0, listOf("https://picsum.photos/seed/l006/400/400")),
            AdminListingListItem("l-007", "Designer Handbag (Replica)", "Isabella Rossi", "u-008", "Fashion", "suspended", 15000, "AUD", "2026-06-13T16:40:00Z", 3, emptyList()),
            AdminListingListItem("l-008", "Herman Miller Aeron Chair", "Charlotte Davies", "u-013", "Furniture", "active", 78000, "AUD", "2026-06-13T13:05:00Z", 0, listOf("https://picsum.photos/seed/l008/400/400")),
            AdminListingListItem("l-009", "Nikon D750 DSLR + 50mm Lens", "Lucas Silva", "u-014", "Electronics", "active", 95000, "AUD", "2026-06-12T20:18:00Z", 1, listOf("https://picsum.photos/seed/l009/400/400")),
            AdminListingListItem("l-010", "PlayStation 5 Slim Disc Edition", "Mohammed Al-Farsi", "u-011", "Gaming", "active", 72000, "AUD", "2026-06-12T15:33:00Z", 0, listOf("https://picsum.photos/seed/l010/400/400")),
            AdminListingListItem("l-011", "IKEA MALM Bed Frame Queen", "Emma Thompson", "u-001", "Furniture", "sold", 18000, "AUD", "2026-06-01T10:00:00Z", 0, listOf("https://picsum.photos/seed/l011/400/400")),
            AdminListingListItem("l-012", "Bose SoundLink Flex Speaker", "Aisha Rahman", "u-006", "Electronics", "active", 12000, "AUD", "2026-06-11T08:12:00Z", 1, listOf("https://picsum.photos/seed/l012/400/400")),
            AdminListingListItem("l-013", "Patagonia Down Sweater Jacket", "Sophia Martinez", "u-005", "Fashion", "active", 16000, "AUD", "2026-06-10T17:45:00Z", 0, listOf("https://picsum.photos/seed/l013/400/400")),
            AdminListingListItem("l-014", "Apple Watch Series 9 45mm", "James Carter", "u-002", "Electronics", "sold", 52000, "AUD", "2026-05-29T12:00:00Z", 0, listOf("https://picsum.photos/seed/l014/400/400")),
        )
        return PaginatedResponse(
            data = listings,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 318, totalPages = 16),
        )
    }

    override suspend fun getListingDetail(listingId: String): AdminListingDetail = AdminListingDetail(
        id = listingId,
        title = "Vintage Leather Chesterfield Sofa",
        description = "Genuine full-grain leather Chesterfield in tan. Some patina consistent with age, structurally excellent. Pickup from Newtown, can help arrange courier. Originally \$2,400 new.",
        priceCents = 120000,
        currency = "AUD",
        status = "active",
        condition = "Used - Good",
        categoryId = "cat-furniture",
        categoryName = "Furniture",
        sellerId = "u-005",
        sellerName = "Sophia Martinez",
        sellerEmail = "sophia.martinez@gmail.com",
        sellerAvatarUrl = "https://i.pravatar.cc/120?img=5",
        imageUrls = listOf(
            "https://picsum.photos/seed/detail-a/800/600",
            "https://picsum.photos/seed/detail-b/800/600",
            "https://picsum.photos/seed/detail-c/800/600",
        ),
        createdAt = "2026-06-15T19:30:00Z",
        updatedAt = "2026-06-16T08:02:00Z",
        reports = listOf(
            ReportResponse("rep-401", listingId, "u-007", "Wrong category", "This looks like it belongs in Antiques, not Furniture.", "pending", "2026-06-16T09:14:00Z", "Vintage Leather Chesterfield Sofa", "Noah Williams"),
        ),
        moderationHistory = listOf(
            ModerationLogEntry("m-501", "Sofia Reyes", "DISMISS_REPORT", "u-005", "Sophia Martinez", listingId, "Vintage Leather Chesterfield Sofa", "Category acceptable", "2026-06-16T10:20:00Z"),
        ),
    )

    override suspend fun getBanPreview(userId: String): BanPreviewResponse = BanPreviewResponse(
        activeListings = 4,
        pendingOffers = 6,
        conversations = 11,
    )

    override suspend fun getPendingReports(page: Int, pageSize: Int): PaginatedResponse<ReportResponse> {
        val reports = listOf(
            ReportResponse("rep-001", "l-004", "u-001", "Counterfeit", "Box and serials don't match Apple's, these are fakes.", "pending", "2026-06-17T08:40:00Z", "Counterfeit AirPods Pro 2", "Emma Thompson"),
            ReportResponse("rep-002", "l-007", "u-005", "Counterfeit", "Clearly a replica handbag sold as authentic.", "pending", "2026-06-16T21:05:00Z", "Designer Handbag (Replica)", "Sophia Martinez"),
            ReportResponse("rep-003", "l-003", "u-007", "Wrong category", "Should be under Antiques.", "pending", "2026-06-16T09:14:00Z", "Vintage Leather Chesterfield Sofa", "Noah Williams"),
            ReportResponse("rep-004", "l-009", "u-013", "Spam", "Reposted five times today.", "pending", "2026-06-15T18:50:00Z", "Nikon D750 DSLR + 50mm Lens", "Charlotte Davies"),
        )
        return PaginatedResponse(
            data = reports,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 5, totalPages = 1),
        )
    }

    override suspend fun getPendingUserReports(page: Int, pageSize: Int): PaginatedResponse<UserReportResponse> {
        val reports = listOf(
            UserReportResponse("urep-001", "u-003", "u-007", "Harassment", "Sent aggressive messages after a declined offer.", "pending", "2026-06-15T11:30:00Z", "Olivia Bennett", "https://i.pravatar.cc/120?img=47", "Noah Williams"),
            UserReportResponse("urep-002", "u-009", "u-005", "Scam", "Asked to pay outside the app via gift cards.", "pending", "2026-06-14T16:22:00Z", "Dmitri Volkov", "https://i.pravatar.cc/120?img=15", "Sophia Martinez"),
        )
        return PaginatedResponse(
            data = reports,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 2, totalPages = 1),
        )
    }

    override suspend fun getImageQueue(page: Int, pageSize: Int, status: ImageStatus?): PaginatedResponse<AdminImageListItem> {
        val images = listOf(
            AdminImageListItem(
                id = "img-001",
                storageUrl = "https://picsum.photos/seed/image-review-001/600/420",
                status = ImageStatus.PENDING,
                bucket = "listing-images",
                uploadedBy = "u-002",
                uploaderName = "James Carter",
                listingId = "l-001",
                listingTitle = "iPhone 13 Pro 256GB Graphite",
                flags = emptyList(),
                confidence = null,
                createdAt = "2026-06-17T10:18:00Z",
                moderatedAt = null,
            ),
            AdminImageListItem(
                id = "img-002",
                storageUrl = "https://picsum.photos/seed/image-review-002/600/420",
                status = ImageStatus.NEEDS_REVIEW,
                bucket = "listing-images",
                uploadedBy = "u-009",
                uploaderName = "Dmitri Volkov",
                listingId = "l-004",
                listingTitle = "Counterfeit AirPods Pro 2",
                flags = listOf(ContentFlag.SPAM, ContentFlag.WEAPONS),
                confidence = 0.82f,
                createdAt = "2026-06-17T09:32:00Z",
                moderatedAt = null,
            ),
            AdminImageListItem(
                id = "img-003",
                storageUrl = "https://picsum.photos/seed/image-review-003/600/420",
                status = ImageStatus.REJECTED,
                bucket = "profile-images",
                uploadedBy = "u-008",
                uploaderName = "Isabella Rossi",
                listingId = null,
                listingTitle = null,
                flags = listOf(ContentFlag.NUDITY),
                confidence = 0.94f,
                createdAt = "2026-06-16T21:05:00Z",
                moderatedAt = "2026-06-16T21:14:00Z",
            ),
        )
        val filtered = status?.let { selected -> images.filter { it.status == selected } } ?: images
        return PaginatedResponse(
            data = filtered,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = filtered.size.toLong(), totalPages = 1),
        )
    }

    override suspend fun getModerationLogs(page: Int, pageSize: Int, adminId: String?, action: String?, sortBy: String?, sortAsc: Boolean, dateFrom: String?, dateTo: String?): PaginatedResponse<ModerationLogEntry> {
        val logs = listOf(
            ModerationLogEntry("ml-001", "Priya Nair", "SUSPEND_LISTING", "u-009", "Dmitri Volkov", "l-004", "Counterfeit AirPods Pro 2", "Counterfeit product", "2026-06-17T09:42:00Z"),
            ModerationLogEntry("ml-002", "Marcus Webb", "BAN_USER", "u-009", "Dmitri Volkov", null, null, "Repeated scam reports", "2026-06-17T09:18:00Z"),
            ModerationLogEntry("ml-003", "Priya Nair", "WARN_USER", "u-004", "Liam O'Connor", null, null, "Spam in messages", "2026-06-17T08:55:00Z"),
            ModerationLogEntry("ml-004", "Sofia Reyes", "DISMISS_REPORT", "u-005", "Sophia Martinez", "l-003", "Vintage Leather Chesterfield Sofa", "No violation found", "2026-06-17T08:31:00Z"),
            ModerationLogEntry("ml-005", "Marcus Webb", "RESTORE_LISTING", "u-002", "James Carter", "l-001", "iPhone 13 Pro 256GB Graphite", "Appeal approved", "2026-06-17T07:49:00Z"),
            ModerationLogEntry("ml-006", "Sofia Reyes", "SUSPEND_USER", "u-012", "Chen Wei", null, null, "Harassment", "2026-06-16T22:14:00Z"),
            ModerationLogEntry("ml-007", "Priya Nair", "SUSPEND_LISTING", "u-008", "Isabella Rossi", "l-007", "Designer Handbag (Replica)", "Counterfeit product", "2026-06-16T20:03:00Z"),
            ModerationLogEntry("ml-008", "Marcus Webb", "WARN_USER", "u-006", "Aisha Rahman", null, null, "Wrong category abuse", "2026-06-16T18:27:00Z"),
            ModerationLogEntry("ml-009", "Sofia Reyes", "RESTORE_USER", "u-008", "Isabella Rossi", null, null, "Suspension expired", "2026-06-16T12:00:00Z"),
            ModerationLogEntry("ml-010", "Priya Nair", "DISMISS_REPORT", "u-001", "Emma Thompson", "l-002", "Sony WH-1000XM5 Headphones", "No violation found", "2026-06-15T15:42:00Z"),
            ModerationLogEntry("ml-011", "Marcus Webb", "SUSPEND_LISTING", "u-014", "Lucas Silva", "l-009", "Nikon D750 DSLR + 50mm Lens", "Duplicate spam listing", "2026-06-15T11:10:00Z"),
            ModerationLogEntry("ml-012", "Sofia Reyes", "WARN_USER", "u-003", "Olivia Bennett", null, null, "Spam in messages", "2026-06-14T09:33:00Z"),
        )
        return PaginatedResponse(
            data = logs,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 86, totalPages = 5),
        )
    }

    override suspend fun dismissReport(reportId: String): Result<Unit> = Result.success(Unit)

    override suspend fun actionReport(reportId: String): Result<Unit> = Result.success(Unit)

    override suspend fun dismissUserReport(reportId: String): Result<Unit> = Result.success(Unit)

    override suspend fun actionUserReport(reportId: String): Result<Unit> = Result.success(Unit)

    override suspend fun approveImage(id: String): Result<Unit> = Result.success(Unit)

    override suspend fun rejectImage(id: String): Result<Unit> = Result.success(Unit)

    override suspend fun suspendListing(listingId: String, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun restoreListing(listingId: String, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun warnUser(userId: String, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun suspendUser(userId: String, durationDays: Int, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun banUser(userId: String, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun restoreUser(userId: String, reason: String?): Result<Unit> = Result.success(Unit)

    override suspend fun getQueue(page: Int, pageSize: Int, source: String?, sort: String?, reason: String?, view: String?): AdminQueueResponse {
        val items = listOf(
            AdminQueueItem.ListingReportItem(
                id = "q-001",
                createdAt = "2026-06-17T09:12:00Z",
                listingId = "l-004",
                listingTitle = "Counterfeit AirPods Pro 2",
                thumbnailUrl = "https://picsum.photos/seed/q001/200/200",
                reporterId = "u-001",
                reporterName = "Emma Thompson",
                reason = "Counterfeit",
                details = "Box and serials don't match Apple's, these are fakes.",
                reporterCount = 3,
                priorActions = 2,
            ),
            AdminQueueItem.UserReportItem(
                id = "q-002",
                createdAt = "2026-06-17T08:47:00Z",
                reportedUserId = "u-009",
                reportedUserName = "Dmitri Volkov",
                reportedUserAvatarUrl = "https://i.pravatar.cc/120?img=15",
                reporterId = "u-005",
                reporterName = "Sophia Martinez",
                reason = "Scam",
                details = "Asked to pay outside the app via gift cards.",
                reporterCount = 4,
                priorActions = 3,
            ),
            AdminQueueItem.ListingReportItem(
                id = "q-003",
                createdAt = "2026-06-17T07:55:00Z",
                listingId = "l-007",
                listingTitle = "Designer Handbag (Replica)",
                thumbnailUrl = "https://picsum.photos/seed/q003/200/200",
                reporterId = "u-005",
                reporterName = "Sophia Martinez",
                reason = "Counterfeit",
                details = "Clearly a replica sold as authentic.",
                reporterCount = 2,
                priorActions = 1,
            ),
            AdminQueueItem.UserReportItem(
                id = "q-004",
                createdAt = "2026-06-16T22:31:00Z",
                reportedUserId = "u-003",
                reportedUserName = "Olivia Bennett",
                reportedUserAvatarUrl = "https://i.pravatar.cc/120?img=47",
                reporterId = "u-007",
                reporterName = "Noah Williams",
                reason = "Harassment",
                details = "Sent aggressive messages after a declined offer.",
                reporterCount = 1,
                priorActions = 1,
            ),
            AdminQueueItem.ListingReportItem(
                id = "q-005",
                createdAt = "2026-06-16T20:18:00Z",
                listingId = "l-003",
                listingTitle = "Vintage Leather Chesterfield Sofa",
                thumbnailUrl = "https://picsum.photos/seed/q005/200/200",
                reporterId = "u-007",
                reporterName = "Noah Williams",
                reason = "Wrong category",
                details = "Should be under Antiques.",
                reporterCount = 1,
                priorActions = 0,
            ),
            AdminQueueItem.ListingReportItem(
                id = "q-006",
                createdAt = "2026-06-16T17:42:00Z",
                listingId = "l-009",
                listingTitle = "Nikon D750 DSLR + 50mm Lens",
                thumbnailUrl = "https://picsum.photos/seed/q006/200/200",
                reporterId = "u-013",
                reporterName = "Charlotte Davies",
                reason = "Spam",
                details = "Reposted five times today.",
                reporterCount = 2,
                priorActions = 0,
            ),
            AdminQueueItem.UserReportItem(
                id = "q-007",
                createdAt = "2026-06-16T14:09:00Z",
                reportedUserId = "u-012",
                reportedUserName = "Chen Wei",
                reportedUserAvatarUrl = "https://i.pravatar.cc/120?img=11",
                reporterId = "u-010",
                reporterName = "Grace Kim",
                reason = "Harassment",
                details = "Repeated unwanted contact.",
                reporterCount = 3,
                priorActions = 2,
            ),
            AdminQueueItem.ActiveListingItem(
                id = "q-008",
                createdAt = "2026-06-16T11:30:00Z",
                title = "PlayStation 5 Slim Disc Edition",
                thumbnailUrl = "https://picsum.photos/seed/q008/200/200",
                sellerId = "u-011",
                sellerName = "Mohammed Al-Farsi",
                priceCents = 72000,
                currency = "AUD",
                reportCount = 0,
                priorActions = 0,
            ),
            AdminQueueItem.ActiveListingItem(
                id = "q-009",
                createdAt = "2026-06-16T09:05:00Z",
                title = "Herman Miller Aeron Chair",
                thumbnailUrl = "https://picsum.photos/seed/q009/200/200",
                sellerId = "u-013",
                sellerName = "Charlotte Davies",
                priceCents = 78000,
                currency = "AUD",
                reportCount = 1,
                priorActions = 0,
            ),
            AdminQueueItem.ListingReportItem(
                id = "q-010",
                createdAt = "2026-06-15T19:48:00Z",
                listingId = "l-012",
                listingTitle = "Bose SoundLink Flex Speaker",
                thumbnailUrl = "https://picsum.photos/seed/q010/200/200",
                reporterId = "u-006",
                reporterName = "Aisha Rahman",
                reason = "Spam",
                details = "Posting same item in many categories.",
                reporterCount = 2,
                priorActions = 1,
            ),
        )
        return AdminQueueResponse(
            data = items,
            meta = PaginationMeta(page = page, pageSize = pageSize, totalCount = 7, totalPages = 1),
        )
    }

    override suspend fun bulkAction(request: BulkActionRequest): Result<BulkActionResponse> = Result.success(
        BulkActionResponse(
            results = request.items.map { BulkActionItemResult(id = it.id, success = true, error = null) },
        )
    )

    override suspend fun getSavedViews(): SavedViewsResponse = SavedViewsResponse(
        views = listOf(
            SavedView("sv-001", "Counterfeit backlog", "reason=Counterfeit&sort=oldest", "2026-05-12T10:00:00Z"),
            SavedView("sv-002", "High-signal user reports", "source=user_report&sort=priority", "2026-06-01T15:30:00Z"),
        )
    )

    override suspend fun putSavedViews(views: List<SavedView>): Result<Unit> = Result.success(Unit)

    override suspend fun listNotes(targetType: String, targetId: String): AdminNotesResponse = AdminNotesResponse(
        notes = listOf(
            AdminNote("n-001", AdminTargetType.fromDbValue(targetType), targetId, "adm-01", "Priya Nair", "Spoke with seller, agreed to relist under correct category. Watching for repeat behaviour.", "2026-06-16T11:20:00Z", null),
            AdminNote("n-002", AdminTargetType.fromDbValue(targetType), targetId, "adm-02", "Marcus Webb", "Second offence this month. Next violation should escalate to suspension.", "2026-06-17T08:05:00Z", "2026-06-17T08:09:00Z"),
        )
    )

    override suspend fun createNote(targetType: String, targetId: String, body: String): Result<AdminNote> = Result.success(
        AdminNote(
            id = "n-new",
            targetType = AdminTargetType.fromDbValue(targetType),
            targetId = targetId,
            authorId = "adm-01",
            authorName = "Priya Nair",
            body = body,
            createdAt = "2026-06-17T10:00:00Z",
            updatedAt = null,
        )
    )

    override suspend fun deleteNote(noteId: String): Result<Unit> = Result.success(Unit)

    override suspend fun listAuditEntries(targetType: String, targetId: String): AuditEntriesResponse = AuditEntriesResponse(
        entries = listOf(
            AuditEntry("au-001", "adm-01", "Priya Nair", "https://i.pravatar.cc/120?img=32", AdminTargetType.fromDbValue(targetType), targetId, "2026-06-17T09:50:00Z"),
            AuditEntry("au-002", "adm-02", "Marcus Webb", "https://i.pravatar.cc/120?img=14", AdminTargetType.fromDbValue(targetType), targetId, "2026-06-17T08:02:00Z"),
            AuditEntry("au-003", "adm-03", "Sofia Reyes", null, AdminTargetType.fromDbValue(targetType), targetId, "2026-06-16T22:31:00Z"),
        )
    )

    override suspend fun recordView(targetType: String, targetId: String): Result<Unit> = Result.success(Unit)

    override suspend fun getUserConversations(userId: String): AdminConversationsResponse = AdminConversationsResponse(
        conversations = listOf(
            AdminConversationSummary("c-001", "l-101", "Mid-century Oak Dining Table", "u-007", "Noah Williams", userId, "Olivia Bennett", "2026-06-16T18:42:00Z", "Is the table still available?", 8),
            AdminConversationSummary("c-002", "l-102", "Le Creuset Dutch Oven 24cm", "u-010", "Grace Kim", userId, "Olivia Bennett", "2026-06-15T12:11:00Z", "Would you take \$150?", 5),
            AdminConversationSummary("c-003", "l-104", "Dyson V8 Cordless Vacuum", "u-005", "Sophia Martinez", userId, "Olivia Bennett", "2026-06-14T09:30:00Z", "Sent you an offer", 3),
        )
    )

    override suspend fun getListingConversations(listingId: String): AdminConversationsResponse = AdminConversationsResponse(
        conversations = listOf(
            AdminConversationSummary("c-101", listingId, "Vintage Leather Chesterfield Sofa", "u-007", "Noah Williams", "u-005", "Sophia Martinez", "2026-06-16T20:05:00Z", "Can you do pickup this weekend?", 6),
            AdminConversationSummary("c-102", listingId, "Vintage Leather Chesterfield Sofa", "u-013", "Charlotte Davies", "u-005", "Sophia Martinez", "2026-06-15T14:48:00Z", "Any flexibility on price?", 4),
        )
    )

    override suspend fun getConversationMessages(conversationId: String): AdminMessagesResponse = AdminMessagesResponse(
        messages = listOf(
            AdminConversationMessage("msg-001", "u-007", "Noah Williams", AdminMessageSenderRole.BUYER, "text", "Hi, is the table still available?", null, false, "2026-06-16T18:42:00Z"),
            AdminConversationMessage("msg-002", "u-003", "Olivia Bennett", AdminMessageSenderRole.SELLER, "text", "Yes it is! Pickup from Newtown.", null, false, "2026-06-16T18:46:00Z"),
            AdminConversationMessage("msg-003", "u-007", "Noah Williams", AdminMessageSenderRole.BUYER, "offer", "Offered \$400", null, false, "2026-06-16T18:50:00Z"),
            AdminConversationMessage("msg-004", "u-003", "Olivia Bennett", AdminMessageSenderRole.SELLER, "text", "Could do \$430, that's my best.", null, false, "2026-06-16T18:55:00Z"),
            AdminConversationMessage("msg-005", null, null, AdminMessageSenderRole.SYSTEM, "system", "Offer accepted", null, false, "2026-06-16T19:02:00Z"),
        )
    )

    override suspend fun getFeatureFlags(): List<FeatureFlagDto> = listOf(
        FeatureFlagDto(
            key = "listing_ai",
            label = "AI listing draft (Snap & List)",
            description = "Generate a listing draft from photos using AI.",
            enabled = false,
        ),
    )

    override suspend fun setFeatureFlag(key: String, enabled: Boolean): Result<Unit> = Result.success(Unit)
}
